import type { ParseKeys } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import lockImg from '@/assets/images/lock.webp';
import surveyImg from '@/assets/images/testwork-large.webp';
import { Button } from '@/components/Button';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  SurveyDetailState,
} from '@/types/components';

type SurveyBodyState = Exclude<SurveyDetailState, SurveyDetailState.Results>;

interface StateVisual {
  image: string;
  titleKey: ParseKeys | null;
  subtitleKey: ParseKeys;
  actionKey: ParseKeys | null;
}

const stateVisuals: Record<SurveyBodyState, StateVisual> = {
  [SurveyDetailState.New]: {
    image: surveyImg,
    titleKey: 'mail.surveys.detail.new.title',
    subtitleKey: 'mail.surveys.detail.new.subtitle',
    actionKey: 'mail.surveys.detail.new.action',
  },
  [SurveyDetailState.Continue]: {
    image: surveyImg,
    titleKey: 'mail.surveys.detail.continue.title',
    subtitleKey: 'mail.surveys.detail.continue.subtitle',
    actionKey: 'mail.surveys.detail.continue.action',
  },
  [SurveyDetailState.Restricted]: {
    image: lockImg,
    titleKey: null,
    subtitleKey: 'mail.surveys.detail.restricted.subtitle',
    actionKey: null,
  },
};

interface Props {
  state: SurveyBodyState;
  questionCount: number;
}

export const SurveyStateBody: React.FC<Props> = ({ state, questionCount }) => {
  const { t } = useTranslation();

  const { image, titleKey, subtitleKey, actionKey } = stateVisuals[state];
  const hasAction = actionKey !== null;

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center md:flex-none md:justify-start md:pt-10">
        <img
          src={image}
          alt=""
          width={120}
          height={120}
          className="size-[120px]"
        />

        {titleKey && (
          <p className="text-callout-16 text-foreground">{t(titleKey)}</p>
        )}

        <p className="text-body-14 max-w-[420px] text-subtle-foreground">
          {t(subtitleKey)}
        </p>

        {hasAction && (
          <span className="text-caption-12 rounded-xl bg-on-primary-surface px-2 py-0.5 text-muted-foreground">
            {t('mail.surveys.detail.questions', { count: questionCount })}
          </span>
        )}
      </div>
    </div>
  );
};

interface ActionProps {
  isMobile: boolean;
  isStarting: boolean;
  state: SurveyBodyState;
  onFill: () => void;
}

export const SurveyStateAction: React.FC<ActionProps> = ({
  isMobile,
  isStarting,
  state,
  onFill,
}) => {
  const { t } = useTranslation();

  const { actionKey } = stateVisuals[state];

  if (!actionKey) {
    return null;
  }

  return (
    <div
      className={
        isMobile
          ? 'shrink-0 border-t border-divider bg-background px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom))]'
          : 'mt-2 flex flex-col items-center'
      }
    >
      <Button
        isLoading={isStarting}
        variant={ButtonVariant.Primary}
        size={isMobile ? Size.Large : Size.Medium}
        radius={isMobile ? BorderRadius.Small : BorderRadius.Pill}
        className={isMobile ? 'h-14 w-full' : ''}
        onClick={onFill}
      >
        {t(actionKey)}
      </Button>
    </div>
  );
};
